import { Link } from "react-router-dom";
import { Instagram, Facebook, Star, ArrowUpRight } from "lucide-react";
import { SITE, TOWNS } from "@/data/site";
import { useLiveStats } from "@/lib/liveStats";
import { FOOTER } from "@/constants/testIds";

export default function Footer() {
  const stats = useLiveStats();
  return (
    <footer
      data-testid={FOOTER.root}
      className="bg-stone-900 text-stone-300 mt-0"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-6">
          <h3 className="font-serif text-[2.4rem] md:text-[3.4rem] text-stone-50 leading-[1.04] tracking-tight">
            Real reviews. Real couples.
            <br />
            <em className="italic text-stone-400">Real films.</em>
          </h3>
          <p className="mt-7 font-serif italic text-stone-400 max-w-md text-lg leading-relaxed">
            An independent review hub showcasing publicly verified ratings of Wedding Video Essex ,
            Essex&apos;s highest-rated wedding videographer and photographer.
          </p>
          <div className="mt-9 flex items-center gap-3">
            <Star className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" strokeWidth={1} />
            <p className="font-serif text-stone-200 text-lg">
              <span className="kbd">{stats.rating.toFixed(1)}</span>
              <span className="italic text-stone-400 ml-2">
                from {stats.total} reviews on Hitched &amp; Google
              </span>
            </p>
          </div>
          <a
            href={SITE.hitchedUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={FOOTER.hitchedLink}
            className="mt-3 inline-flex items-center gap-1.5 font-serif italic text-stone-400 hover:text-stone-200 transition-colors"
          >
            view sources
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-stone-500 mb-6">Essex areas</p>
          <ul className="space-y-3 font-serif italic">
            {TOWNS.map((t) => (
              <li key={t.slug}>
                <Link
                  to={`/wedding-videographer-${t.slug}`}
                  className="text-stone-300 hover:text-white transition-colors"
                  data-testid={`footer-town-${t.slug}`}
                >
                  {t.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="overline text-stone-500 mb-6">About</p>
          <ul className="space-y-3 font-serif italic">
            <li>
              <Link to="/about" className="text-stone-300 hover:text-white transition-colors" data-testid="footer-about">
                About Alex &amp; the team
              </Link>
            </li>
            <li>
              <Link to="/reviews" className="text-stone-300 hover:text-white transition-colors" data-testid="footer-reviews">
                All {stats.total} reviews
              </Link>
            </li>
          </ul>

          <p className="overline text-stone-500 mb-6 mt-10">Get in touch</p>
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={FOOTER.bookCta}
            className="inline-flex items-center gap-2 text-stone-50 font-serif italic text-xl border-b border-stone-600 hover:border-stone-200 pb-1 transition-colors"
          >
            Check my date
            <ArrowUpRight className="w-4 h-4" />
          </a>
          <div className="mt-10 flex items-center gap-5">
            <a
              href="https://www.instagram.com/weddingvideoessex/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram"
              className="text-stone-400 hover:text-white transition-colors"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.2} />
            </a>
            <a
              href="https://www.facebook.com/essexweddingvideo"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="footer-facebook"
              className="text-stone-400 hover:text-white transition-colors"
            >
              <Facebook className="w-5 h-5" strokeWidth={1.2} />
            </a>
          </div>
          <p className="mt-10 font-serif italic text-sm text-stone-500 leading-relaxed">
            All reviews are publicly published on Hitched, Google, Bridebook, Facebook and Guides for Brides and link back
            to the source. Service area: {SITE.serviceArea}.
          </p>
          <p className="mt-5 font-serif italic text-sm">
            <a
              href="https://www.wvegroup.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-wve-group"
              className="text-stone-400 hover:text-stone-200 border-b border-stone-700 hover:border-stone-300 transition-colors pb-0.5"
            >
              Part of WVE Group
            </a>
          </p>
        </div>
      </div>

      <div className="border-t border-stone-800/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-7 flex flex-col md:flex-row items-center justify-between text-xs text-stone-500 font-serif italic">
          <p>© {new Date().getFullYear()} {SITE.domain}.</p>
          <p className="mt-2 md:mt-0">
            Based in Wickford, Essex · Filming across Essex, London, Kent &amp; Suffolk
          </p>
        </div>
      </div>
    </footer>
  );
}
